/**
 * NFC Solutions — Coordinator ROLE client (Session 27.53+).
 *
 * This module owns the *client half* of the Coordinator election
 * added in Session 27.53. It is distinct from `./coordinator.js` —
 * that file describes a future LAN-transport interface. This file
 * is exclusively about "who is today's coordinator?".
 *
 * Responsibilities:
 *   1. Talk to the five `/api/coordinator/*` endpoints.
 *   2. Cache today's decision in sessionStorage so we don't hammer
 *      the backend on every tab focus.
 *   3. Drive a 30s heartbeat loop while THIS device holds the role.
 *   4. Expose a small imperative API that App.js can consume from a
 *      React hook without needing to know any of the plumbing.
 *
 * The election / heartbeat is only meaningful in an installed
 * context — this module never runs in a regular browser tab. That
 * gate is applied at the CALL SITE (see `useCoordinatorElection`
 * in App.js) so the module itself stays pure.
 *
 * IMPORTANT: this module NEVER throws. Every error is swallowed and
 * surfaced as a `null` return so the UI can keep the restaurant
 * running even if the coordinator API is temporarily unreachable.
 */

const HEARTBEAT_INTERVAL_MS = 30 * 1000;
const SESSION_KEY_LAST_CHECK = "nfcs:coordinator:last_check";
const SESSION_KEY_SKIPPED = "nfcs:coordinator:skipped_today";
// Session 27.72 — Per-device "we already asked today" hard lock. This
// stops the election modal from re-appearing on every sign-in even
// when the operator gave a definitive answer (Yes / Skip / Dismiss)
// earlier the same day. The key is scoped by device_id + business_id
// + YYYY-MM-DD so a device that flips businesses (rare, but possible
// during onboarding) doesn't inherit yesterday's decision.
function _todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function _askedKey(deviceId, businessId) {
  return `nfcs:coordinator:asked:${deviceId || "unknown"}:${businessId || "any"}:${_todayKey()}`;
}
export function markAskedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(_askedKey(deviceId, businessId), "1");
  } catch (_) { /* noop */ }
}
function _wasAskedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(_askedKey(deviceId, businessId)) === "1";
  } catch (_) { return false; }
}
/** Clear today's "asked" lock so the next evaluate cycle re-prompts.
 * Used by the "Rescan LAN & coordinator" button on the status pill. */
export function clearAskedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(_askedKey(deviceId, businessId));
    // Also clear the sessionStorage "skipped" fast-path so a manual
    // rescan is truly a clean slate — otherwise the same session
    // would still short-circuit at `hasSkippedToday`.
    try { window.sessionStorage.removeItem(SESSION_KEY_SKIPPED); } catch (_) { /* noop */ }
  } catch (_) { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────────
// Session 30.1 — Per-business-day "claimed today" persistence.
//
// The operator's core complaint: a device that already said "Yes, I am
// today's Coordinator" gets re-asked when they switch dashboards
// (Kitchen → Bar), when the app refreshes, or after an offline stint
// long enough for the server-side 20-minute heartbeat grace to expire.
//
// Fix: the moment this device becomes coordinator (explicit Yes OR
// silent reclaim), we stamp a localStorage flag scoped by
// device + business + YYYY-MM-DD. For the REST of the business day:
//   • If the server record expired/vanished, we silently re-claim
//     WITHOUT prompting.
//   • If the backend is unreachable (offline mode), we resume the
//     coordinator role locally so LAN routing + local queueing keep
//     working, and the heartbeat re-registers us once internet returns.
// The date inside the key means the flag AUTO-RESETS the next morning —
// exactly the "per business day" memory the operator asked for.
// The flag is cleared ONLY by an explicit "Hand off Coordinator".
// ─────────────────────────────────────────────────────────────────────
function _claimedKey(deviceId, businessId) {
  return `nfcs:coordinator:claimed:${deviceId || "unknown"}:${businessId || "any"}:${_todayKey()}`;
}
export function markClaimedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(_claimedKey(deviceId, businessId), "1");
  } catch (_) { /* noop */ }
}
export function wasClaimedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(_claimedKey(deviceId, businessId)) === "1";
  } catch (_) { return false; }
}
export function clearClaimedToday(deviceId, businessId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(_claimedKey(deviceId, businessId));
  } catch (_) { /* noop */ }
}

/**
 * Small helper — the backend base URL is the same everywhere the
 * app talks to it. We import it lazily so this module can be unit-
 * tested without a bundler being involved.
 */
function _apiBase() {
  try {
    return (
      (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL)
      || (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL)
      || ""
    );
  } catch (_) {
    return "";
  }
}

async function _apiFetch(path, { method = "GET", body, token, timeoutMs = 8000 } = {}) {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => { try { controller.abort(); } catch (_) { /* noop */ } }, timeoutMs)
    : null;
  try {
    const url = `${_apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller ? controller.signal : undefined,
      credentials: "omit",
    });
    if (timeoutId) clearTimeout(timeoutId);
    // 409 is expected for "already claimed" — surface it so callers
    // can react without seeing it as an error.
    let json = null;
    try { json = await res.json(); } catch (_) { /* body may be empty */ }
    return { ok: res.ok, status: res.status, data: json };
  } catch (err) {
    if (timeoutId) clearTimeout(timeoutId);
    return { ok: false, status: 0, data: null, error: err };
  }
}

/**
 * Fetch the current coordinator for this business/day. Returns the
 * coordinator object (with `device_id`, `device_label`, …) or `null`
 * if nobody holds the role.
 */
export async function fetchCurrentCoordinator(token) {
  const r = await _apiFetch("/api/coordinator/current", { token });
  if (!r.ok) return null;
  return r.data && r.data.coordinator ? r.data.coordinator : null;
}

/**
 * Session 30.1 — Like `fetchCurrentCoordinator`, but distinguishes
 * "the backend told us there is no coordinator" (reachable: true,
 * coordinator: null) from "we couldn't reach the backend at all"
 * (reachable: false). The election logic MUST treat these differently:
 * an unreachable backend means we're offline and should fall back to
 * the device-local `wasClaimedToday` memory instead of concluding
 * "no coordinator exists".
 */
export async function fetchCurrentCoordinatorDetailed(token) {
  const r = await _apiFetch("/api/coordinator/current", { token });
  if (!r.ok) {
    // status 0 = network failure / timeout → definitively unreachable.
    // Any HTTP error (401/5xx) also means we can't trust the answer.
    return { reachable: false, coordinator: null, httpStatus: r.status || 0 };
  }
  return {
    reachable: true,
    coordinator: r.data && r.data.coordinator ? r.data.coordinator : null,
    httpStatus: r.status,
  };
}

/**
 * Attempt to claim the Coordinator role for THIS device.
 *
 * Returns one of:
 *   { status: "claimed",         coordinator }
 *   { status: "silent_reclaim",  coordinator }  ← same device, <20 min gap
 *   { status: "already_yours",   coordinator }
 *   { status: "taken",           coordinator }  ← 409, someone else holds it
 *   { status: "error", httpStatus, detail, error }  ← anything else
 *
 * Session 27.62 — `error` now carries the HTTP status code and the
 * server's `detail` (or the JS error message) so the UI can show a
 * useful toast instead of the previous generic "please try again".
 */
export async function claimCoordinator({ token, deviceId, deviceLabel, platform }) {
  const r = await _apiFetch("/api/coordinator/claim", {
    method: "POST",
    body: { device_id: deviceId, device_label: deviceLabel, platform },
    token,
  });
  if (r.status === 409) {
    const other = r.data && r.data.detail && r.data.detail.coordinator ? r.data.detail.coordinator : null;
    return { status: "taken", coordinator: other };
  }
  // Session 27.68 — Bug fix: don't treat a 2xx response as an error
  // just because the JSON body couldn't be parsed. That was showing
  // false-positive "Coordinator claim failed (HTTP 200)" toasts when
  // the server responded successfully but with a non-JSON body or an
  // empty body (some proxies strip Content-Type on redirects). If
  // `r.ok` is true we trust the HTTP status and treat missing data
  // as a successful bare claim.
  if (!r.ok) {
    // Extract a human-readable failure reason. Backend errors carry
    // a `detail` field; fetch/timeout carry an `.error` object.
    let detail = "";
    try {
      if (r.data && r.data.detail) {
        detail = typeof r.data.detail === "string"
          ? r.data.detail
          : (r.data.detail.code || JSON.stringify(r.data.detail));
      } else if (r.error) {
        detail = r.error.name === "AbortError"
          ? "Request timed out"
          : (r.error.message || String(r.error));
      } else if (r.status === 0) {
        detail = "Network unreachable";
      } else if (r.status) {
        detail = `Server returned HTTP ${r.status}`;
      }
    } catch (_) { detail = ""; }
    return { status: "error", httpStatus: r.status || 0, detail, error: r.error || null };
  }
  // 2xx with no parseable body — treat as a successful bare claim.
  const data = r.data || {};
  if (data.already_claimed_by_you) return { status: "already_yours", coordinator: data.coordinator };
  if (data.silent_reclaim) return { status: "silent_reclaim", coordinator: data.coordinator };
  return { status: "claimed", coordinator: data.coordinator || { device_id: deviceId, device_label: deviceLabel, platform } };
}

/**
 * Ping the coordinator heartbeat. Returns `true` on success, `false`
 * on any failure (network error, or a 409 saying we're not the
 * coordinator anymore — in which case the caller should stop the
 * heartbeat loop and check the current coordinator again).
 */
export async function heartbeatCoordinator({ token, deviceId }) {
  const r = await _apiFetch("/api/coordinator/heartbeat", {
    method: "POST",
    body: { device_id: deviceId },
    token,
  });
  if (r.status === 409) return { ok: false, dethroned: true };
  return { ok: !!r.ok, dethroned: false };
}

/**
 * Voluntary release — called from the explicit logout path when the
 * coordinator device is being taken offline. Server marks the row
 * `released` (not deleted) so the 20-minute silent-reclaim window
 * still applies.
 */
export async function releaseCoordinator({ token, deviceId }) {
  const r = await _apiFetch("/api/coordinator/release", {
    method: "POST",
    body: { device_id: deviceId },
    token,
  });
  return !!r.ok;
}

/**
 * Records that THIS device chose "Skip" on today's election so the
 * modal doesn't reappear on this device today. Uses both a server-
 * side record (so it survives app relaunch) and a sessionStorage
 * hint (so the check is instant on the next dashboard mount).
 */
export async function skipCoordinatorElection({ token, deviceId }) {
  try { window.sessionStorage.setItem(SESSION_KEY_SKIPPED, "1"); } catch (_) { /* noop */ }
  const r = await _apiFetch("/api/coordinator/skip", {
    method: "POST",
    body: { device_id: deviceId },
    token,
  });
  return !!r.ok;
}

/**
 * Has this device already skipped today's election? Fast path checks
 * sessionStorage; if that's not set we still ask the server so a
 * device that skipped in the morning and reopens the app in the
 * evening doesn't get re-prompted.
 */
export async function hasSkippedToday({ token, deviceId }) {
  try {
    if (window.sessionStorage.getItem(SESSION_KEY_SKIPPED) === "1") return true;
  } catch (_) { /* ignore */ }
  const r = await _apiFetch(
    `/api/coordinator/skipped?device_id=${encodeURIComponent(deviceId)}`,
    { token },
  );
  return !!(r.ok && r.data && r.data.skipped);
}

/**
 * A tiny heartbeat driver. Call `start()` when this device claims the
 * role; call `stop()` on logout / dethronement / app close. Multiple
 * concurrent `start()`s are safe — the second call is a no-op.
 */
export function createHeartbeatLoop({ token, deviceId, onDethroned }) {
  let timer = null;
  let stopped = false;

  const tick = async () => {
    if (stopped) return;
    const r = await heartbeatCoordinator({ token, deviceId });
    if (r.dethroned && typeof onDethroned === "function") {
      try { onDethroned(); } catch (_) { /* never let the callback kill the loop */ }
      stop();
      return;
    }
  };

  const start = () => {
    if (timer !== null) return;
    // Send the first heartbeat immediately so the server knows we're
    // alive within the same second we claimed — no dead-window where
    // another device could try to claim before our first tick.
    tick();
    timer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
  };

  const stop = () => {
    stopped = true;
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  return { start, stop };
}

/**
 * Convenience wrapper used by the App.js election gate:
 *
 *   const outcome = await runCoordinatorElection({ token, deviceId, deviceLabel, platform });
 *
 * The outcome is one of:
 *   { action: "silent-continue" }    — no prompt needed, we're either
 *                                      not a candidate or someone
 *                                      already holds the role.
 *   { action: "silent-reclaim", coordinator }
 *                                    — this device was coordinator
 *                                      earlier today, dropped for
 *                                      <20 minutes, and is now
 *                                      restored without a prompt.
 *   { action: "prompt", currentCoordinator: null }
 *                                    — the caller should show the
 *                                      "Is this device the Coordinator
 *                                      for today?" modal.
 */
export async function evaluateCoordinatorElection({ token, deviceId, deviceLabel, platform, businessId }) {
  // Session 30.1 — Offline-first evaluation. Distinguish "backend
  // unreachable" from "no coordinator exists" so an internet outage
  // can never strip this device of a role it already claimed today.
  const probe = await fetchCurrentCoordinatorDetailed(token);
  if (!probe.reachable) {
    if (wasClaimedToday(deviceId, businessId) && !_isHandoffCooldownActive(deviceId)) {
      // We claimed the role earlier today and the cloud is down —
      // resume coordinator duties locally. LAN routing + the local
      // event queue keep the restaurant running; the heartbeat loop
      // will re-register us server-side the moment internet returns.
      return {
        action: "silent-reclaim",
        offline: true,
        coordinator: { device_id: deviceId, device_label: deviceLabel || null, business_id: businessId || null },
      };
    }
    // Session 31.4 — Offline and never claimed today: ask the LAN who is
    // coordinator (native document store on the signalling server).
    // Someone else → stay a leaf silently. Nobody → the first device to
    // sign in is asked ONCE whether it should take the role, exactly
    // like the online flow ("first device opens → coordinator").
    try {
      const lan = await import("./lanBootstrap");
      const found = await lan.findLanCoordinator({ scanMs: 2500 });
      if (found && found.info && found.info.device_id && found.info.device_id !== deviceId) {
        return {
          action: "silent-continue",
          offline: true,
          reason: "lan-coordinator-present",
          coordinator: { device_id: found.info.device_id, device_label: found.info.device_label || null, business_id: businessId || null, lan_host: found.host },
        };
      }
    } catch (_) { /* LAN probe is best-effort */ }
    // Re-ask at most every 10 minutes while offline (a "No" earlier today
    // must not stop this device from stepping up after the coordinator
    // tablet logs out later in the shift).
    try {
      const last = Number(window.sessionStorage.getItem("nfcs:coordinator:offline_prompt_at") || 0);
      if (last && Date.now() - last < 10 * 60 * 1000) {
        return { action: "silent-continue", offline: true, reason: "offline-asked-recently" };
      }
    } catch (_) { /* ignore */ }
    if (_isHandoffCooldownActive(deviceId)) {
      return { action: "silent-continue", offline: true, reason: "handoff-cooldown" };
    }
    try { window.sessionStorage.setItem("nfcs:coordinator:offline_prompt_at", String(Date.now())); } catch (_) { /* ignore */ }
    return { action: "prompt", offline: true, reason: "offline-no-lan-coordinator" };
  }
  const current = probe.coordinator;
  // Session 27.64 — Respect the manual hand-off cooldown. If the
  // operator just tapped "Hand off Coordinator" on this device in the
  // last 60 seconds, we deliberately do NOT auto-reclaim (the whole
  // point of the hand-off was to let a different device take over).
  // We also don't prompt the modal in that window — the person who
  // just handed off doesn't want to be asked again immediately.
  const cooldownActive = _isHandoffCooldownActive(deviceId);
  if (current && current.device_id === deviceId) {
    if (cooldownActive) {
      // We're still listed as coordinator server-side but we just
      // asked to be released. Force-release again silently and
      // continue as a leaf. The 20-min grace window still applies
      // if the user changes their mind — the cooldown is purely
      // about "don't re-prompt me for 60 seconds".
      try { await releaseCoordinator({ token, deviceId }); } catch (_) { /* noop */ }
      return { action: "silent-continue" };
    }
    // We were already coordinator today — silent reclaim on refresh.
    markClaimedToday(deviceId, businessId);
    return { action: "silent-reclaim", coordinator: current };
  }
  if (current) {
    // Someone else already owns the role. Nothing to do.
    return { action: "silent-continue", coordinator: current };
  }
  if (cooldownActive) {
    // No coordinator right now AND we just handed off — treat this
    // exactly like a Skip so the next device to sign in gets asked
    // instead of the person who just released it.
    return { action: "silent-continue" };
  }
  // Session 30.1 — Per-business-day claimed memory. This device was
  // ALREADY confirmed as coordinator earlier today (operator tapped
  // Yes, or a silent reclaim happened). The server record has since
  // expired (e.g. an offline stint longer than the 20-minute
  // heartbeat grace, or an overnight app close and re-open on the
  // same business day). Re-claim SILENTLY — never re-prompt a device
  // that already answered "Yes" today. The flag auto-resets tomorrow.
  if (wasClaimedToday(deviceId, businessId)) {
    const reclaim = await claimCoordinator({ token, deviceId, deviceLabel, platform });
    if (reclaim.status === "claimed" || reclaim.status === "silent_reclaim" || reclaim.status === "already_yours") {
      return { action: "silent-reclaim", coordinator: reclaim.coordinator };
    }
    if (reclaim.status === "taken") {
      return { action: "silent-continue", coordinator: reclaim.coordinator };
    }
    // Claim errored (flaky network) — resume locally anyway; the
    // heartbeat loop will register us as soon as the backend answers.
    return {
      action: "silent-reclaim",
      offline: true,
      coordinator: { device_id: deviceId, device_label: deviceLabel || null, business_id: businessId || null },
    };
  }
  // Session 27.72 — Per-device "asked today" hard lock. If we've
  // already surfaced the election modal on this device today (for
  // this business), don't ask again — regardless of whether the
  // operator said Yes, Skip, or dismissed by tapping outside. The
  // user can force a re-prompt from the "Rescan LAN & coordinator"
  // action on the status pill dropdown.
  if (_wasAskedToday(deviceId, businessId)) {
    return { action: "silent-continue" };
  }
  // No active coordinator. Check if we skipped today already.
  const skipped = await hasSkippedToday({ token, deviceId });
  if (skipped) {
    return { action: "silent-continue" };
  }
  // Try a silent claim first — the backend awards `silent_reclaim`
  // when THIS device held the role earlier and the 20-min grace
  // window has not elapsed. If we get that, no prompt; if we don't,
  // we release the row we just accidentally acquired and prompt.
  //
  // Session 27.75 — Peer-discovery window. When multiple devices
  // sign in together, we now wait up to 3.5 seconds while polling
  // `/api/coordinator/current` every ~350ms — this lets whichever
  // device pings the backend first "win" and lets the others see
  // that win *before* attempting their own claim. The device_id
  // hash provides the initial stagger so the fastest claim is
  // deterministic (avoids two devices both hitting exactly at 0ms).
  //
  // In practice: the winner claims within ~200ms, the losers see
  // the record within 500-800ms of their own poll and silent-
  // continue — no prompt on any device except the one that wins.
  try {
    const idStr = String(deviceId || "");
    let hash = 0;
    for (let i = 0; i < idStr.length; i++) hash = ((hash * 31) + idStr.charCodeAt(i)) & 0x7fffffff;
    const initialStaggerMs = 100 + (hash % 400); // 100-500ms deterministic per device
    await new Promise((r) => setTimeout(r, initialStaggerMs));
  } catch (_) { /* noop */ }

  // Discovery poll — up to ~3.5s total, checks every 350ms. As soon
  // as we see a foreign coordinator record we silent-continue.
  const POLL_INTERVAL_MS = 350;
  const POLL_ATTEMPTS = 10; // ~3.5s
  for (let i = 0; i < POLL_ATTEMPTS; i++) {
    let seen = null;
    try { seen = await fetchCurrentCoordinator(token); } catch (_) { seen = null; }
    if (seen && seen.device_id !== deviceId) {
      // Someone else won the race — join silently.
      return { action: "silent-continue", coordinator: seen };
    }
    if (seen && seen.device_id === deviceId) {
      // (Shouldn't happen at this stage but be defensive.) Take it.
      return { action: "silent-reclaim", coordinator: seen };
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }

  const attempt = await claimCoordinator({ token, deviceId, deviceLabel, platform });
  if (attempt.status === "silent_reclaim" || attempt.status === "already_yours") {
    return { action: "silent-reclaim", coordinator: attempt.coordinator };
  }
  if (attempt.status === "taken") {
    return { action: "silent-continue", coordinator: attempt.coordinator };
  }
  if (attempt.status === "claimed") {
    // We just successfully claimed the role, but there was no prior
    // record for us today — the user never explicitly said Yes. Rewind
    // the claim so the user gets asked, and show the prompt.
    //
    // Session 27.76 — Double-check re-verification. Even though the
    // backend now atomically reconciles duplicates (see server.py
    // Session 27.76), we do one final belt-and-braces safety check
    // here. Wait 250ms for any concurrent claim to land, then re-read
    // `/current`. If another device is the coordinator right now,
    // silently continue instead of prompting.
    await new Promise((r) => setTimeout(r, 250));
    let verify = null;
    try { verify = await fetchCurrentCoordinator(token); } catch (_) { verify = null; }
    if (verify && verify.device_id !== deviceId) {
      // Someone else's claim landed first — respect it.
      try { await releaseCoordinator({ token, deviceId }); } catch (_) { /* noop */ }
      return { action: "silent-continue", coordinator: verify };
    }
    // We are the confirmed winner. Rewind the auto-claim so the user
    // sees the "Should this device be the Coordinator?" prompt.
    await releaseCoordinator({ token, deviceId });
    markAskedToday(deviceId, businessId);
    return { action: "prompt", currentCoordinator: null };
  }
  // Session 27.72 — Error path safety net. Previously we fell through
  // to `prompt` on network errors, which is exactly why devices with
  // flaky Wi-Fi kept re-showing the election modal after every login.
  // Now we silently continue on errors: the operator can always force
  // an election via the pill's "Rescan LAN & coordinator" action.
  return { action: "silent-continue" };
}

// ─────────────────────────────────────────────────────────────────────
// Session 27.64 — Hand-off cooldown.
//
// After the operator taps "Hand off Coordinator" on the pill dropdown
// we record a per-device localStorage flag that expires 60 seconds
// later. `evaluateCoordinatorElection` above respects the flag and
// silently continues instead of either (a) silently reclaiming the
// role from the server-side 20-minute grace window, or (b) prompting
// again on the same device. Without this, tapping Hand-off would be
// undone the moment the same operator refreshed the tab.
//
// The cooldown is intentionally short (60s): if the operator changes
// their mind or nobody else takes over, they can sign in again after
// a minute and reclaim the role via the modal.
// ─────────────────────────────────────────────────────────────────────
const _HANDOFF_COOLDOWN_MS = 60 * 1000;
function _handoffKey(deviceId) {
  return `nfcs:coordinator:handoff_cooldown:${deviceId || "unknown"}`;
}
export function markHandoffCooldown(deviceId, ttlMs = _HANDOFF_COOLDOWN_MS) {
  try {
    if (typeof localStorage === "undefined" || !deviceId) return;
    const until = Date.now() + Math.max(1000, ttlMs);
    localStorage.setItem(_handoffKey(deviceId), String(until));
  } catch (_) { /* noop */ }
}
function _isHandoffCooldownActive(deviceId) {
  try {
    if (typeof localStorage === "undefined" || !deviceId) return false;
    const raw = localStorage.getItem(_handoffKey(deviceId));
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    if (Date.now() >= until) {
      // Expired — clean up.
      try { localStorage.removeItem(_handoffKey(deviceId)); } catch (_) { /* noop */ }
      return false;
    }
    return true;
  } catch (_) { return false; }
}
/** Read the number of milliseconds remaining on the cooldown, or 0. */
export function getHandoffCooldownRemainingMs(deviceId) {
  try {
    if (typeof localStorage === "undefined" || !deviceId) return 0;
    const raw = localStorage.getItem(_handoffKey(deviceId));
    if (!raw) return 0;
    const until = Number(raw);
    if (!Number.isFinite(until)) return 0;
    return Math.max(0, until - Date.now());
  } catch (_) { return 0; }
}
